import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { extractSsoCode, stripSsoCode } from '../../context/ssoNavigation'

/**
 * Global SSO completion gate (plan §Stage 3). The SSO callback redirects the browser to `${return_to}?sso_code=<code>`,
 * which can be ANY in-app path — so this mounts once at the router root and watches every route. When it sees a one-time
 * completion code it redeems it (POST /api/auth/sso/complete, bearer returned in the BODY), then navigates to the
 * server-provided return_to (which no longer carries the code). On failure (expired / replayed / wrong browser) it scrubs
 * the code and lands the user on a clean /login. Renders nothing.
 */
export default function SsoCompletionGate() {
  const { completeSsoLogin } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  // A per-code guard that PERSISTS across the effect's cleanup→setup cycle. StrictMode (dev) runs setup→cleanup→setup on the
  // same instance (refs survive), so this makes the one-time code redeem EXACTLY once. Crucially we do NOT gate the navigation
  // on a per-run `cancelled` flag — the first run's cleanup would set it and the (StrictMode) completion would resolve with
  // nav suppressed, stranding the user on the sso_code URL. The redemption is a real side effect: once it resolves we always
  // navigate to the server-provided return_to (which is independent of the current URL, so it survives any guard redirect).
  const handledCode = useRef(null)

  useEffect(() => {
    const code = extractSsoCode(location.search)
    if (!code || handledCode.current === code) return
    handledCode.current = code
    const searchAtStart = location.search
    ;(async () => {
      try {
        const { returnTo } = await completeSsoLogin(code)
        navigate(returnTo || '/dashboard', { replace: true })
      } catch {
        navigate(`/login${stripSsoCode(searchAtStart)}`, { replace: true })
      }
    })()
  }, [location.search, completeSsoLogin, navigate])

  return null
}
